import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, ShieldAlert, Instagram, Phone, Clock, Check, RotateCcw } from 'lucide-react';
import type { Notification } from '../hooks/useNotifications';

interface NotificationPanelProps {
  notifications: Notification[];
  dismissedNotifications: Notification[];
  onDismiss: (id: string) => void;
  onUndismiss: (id: string) => void;
  onClose: () => void;
}

const typeConfig: Record<Notification['type'], { icon: React.ElementType; color: string }> = {
  pending_videos: { icon: Video, color: 'text-red-400' },
  content_call: { icon: Phone, color: 'text-yellow-400' },
  campaign_ending: { icon: Clock, color: 'text-orange-400' },
  meta_access: { icon: ShieldAlert, color: 'text-red-400' },
  no_creatives: { icon: Instagram, color: 'text-red-400' },
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  dismissedNotifications,
  onDismiss,
  onUndismiss,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleNavigate = (n: Notification) => {
    navigate('/dashboard');
    onClose();
  };

  const total = notifications.length + dismissedNotifications.length;

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700">
        <span className="text-sm font-semibold text-white">
          Notifications {total > 0 ? `(${notifications.length}/${total})` : ''}
        </span>
      </div>

      {total === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          Keine offenen Aufgaben
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {/* Active notifications */}
          {notifications.map((n) => {
            const config = typeConfig[n.type];
            const Icon = config.icon;
            return (
              <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                {n.type !== 'pending_videos' && (
                  <button
                    onClick={() => onDismiss(n.id)}
                    className="mt-0.5 flex-shrink-0 w-5 h-5 rounded border border-gray-600 hover:border-green-500 hover:bg-green-500/20 flex items-center justify-center transition-colors"
                    title="Als erledigt markieren"
                  >
                    <Check className="w-3 h-3 text-transparent hover:text-green-400" />
                  </button>
                )}
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleNavigate(n)}
                >
                  <div className="text-sm text-white font-medium truncate">{n.artistName}</div>
                  <div className="text-xs text-gray-400">{n.message}</div>
                </div>
              </div>
            );
          })}

          {/* Dismissed notifications */}
          {dismissedNotifications.length > 0 && (
            <>
              <div className="px-4 py-2 bg-gray-800/50">
                <span className="text-xs text-gray-500 font-medium">Erledigt ({dismissedNotifications.length})</span>
              </div>
              {dismissedNotifications.map((n) => {
                const config = typeConfig[n.type];
                const Icon = config.icon;
                return (
                  <div key={n.id} className="flex items-start gap-3 px-4 py-2 border-b border-gray-800/50 opacity-50 hover:opacity-75 transition-opacity">
                    <button
                      onClick={() => onUndismiss(n.id)}
                      className="mt-0.5 flex-shrink-0 w-5 h-5 rounded border border-green-600 bg-green-500/20 flex items-center justify-center hover:bg-red-500/20 hover:border-red-500 transition-colors"
                      title="Zurücksetzen"
                    >
                      <Check className="w-3 h-3 text-green-400" />
                    </button>
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 text-gray-600`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-500 font-medium truncate line-through">{n.artistName}</div>
                      <div className="text-xs text-gray-600">{n.message}</div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};
